import { useState } from 'react'

import { BarChart } from '@mantine/charts'
import { Badge, Collapse, Modal, Paper, SegmentedControl, Skeleton, Table, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { ChevronDown, ChevronRight, Info } from 'lucide-react'
import { Metadata, useQuery } from '@redwoodjs/web'

import { useLazyQuery } from '@apollo/client'
import { navigate, routes } from '@redwoodjs/router'

import DateRangeMenu from 'src/components/Overlays/DateRangeMenu'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  GET_CORRECTED_DOCUMENTS,
  GET_DISCREPANCY_DOCUMENTS,
  GET_DISCREPANCY_STATS,
  GET_DOCUMENT_INTELLIGENCE_STATS,
  GET_MVR_DISCREPANCY_DOCUMENTS,
  GET_MVR_DISCREPANCY_STATS,
  GET_MVR_EXTRACTION_SUMMARY,
  GET_PSP_DISCREPANCY_DOCUMENTS,
  GET_PSP_DISCREPANCY_STATS,
  GET_PSP_EXTRACTION_SUMMARY,
  GET_PENDING_REVIEW_DOCUMENTS,
} from 'src/graphql/documentIntelligenceAnalytics.graphql'

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined, suffix = '') =>
  n == null ? '—' : `${n.toLocaleString()}${suffix}`

const fmtPct = (n: number | null | undefined) =>
  n == null ? '—' : Number(n.toFixed(1)).toLocaleString()

const CATEGORY_LABELS: Record<string, string> = {
  CDL: 'CDL',
  MVR: 'MVR',
  PSP: 'PSP',
  DOT_APP: 'DOT App',
  DOT_MEDICAL: 'DOT Medical',
  BACKGROUND_CHECK: 'Background Check',
  CLEARING_HOUSE: 'Clearing House',
  CDLIS: 'CDLIS',
  VERIFICATION_OF_EMPLOYMENT: 'VoE',
  DRUG_AND_ALCOHOL_TESTING: 'Drug & Alcohol',
  MEDICAL_EXAMINATION: 'Medical Exam',
  PAPERWORK: 'Paperwork',
  ORIENTATION: 'Orientation',
  HANDBOOK: 'Handbook',
  CONSENT: 'Consent',
  ROAD_TEST: 'Road Test',
  TWIC_CARD: 'TWIC Card',
  OTHER: 'Other',
  DAC: 'DAC',
  INSPECTION: 'Inspection',
  JOB_OFFER: 'Job Offer',
  PAYROLL: 'Payroll',
  TRAINING: 'Training',
}
const catLabel = (v: string) => CATEGORY_LABELS[v] ?? v

const SECONDS_PER_DOCUMENT = 45

const FIELD_LABELS: Record<string, string> = {
  CATEGORY: 'Category',
  DATE: 'Expiration Date',
  FILE_NAME: 'File Name',
  DOCUMENT_SIDE: 'Document Side',
  LICENSE_NUMBER: 'License Number',
  LICENSE_STATE: 'License State',
  LICENSE_CLASS: 'License Class',
  LICENSE_ENDORSEMENTS: 'Endorsements',
  MEDICAL_DATE: 'Medical Expiration',
  INCIDENTS_COUNT: 'Accident/Violation Count',
  SUSPENSION_UNDISCLOSED: 'Undisclosed Suspension',
  PSP_INCIDENTS_COUNT: 'PSP Crash/Violation Count',
}

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  suffix,
  sub,
  info,
  loading,
  onClick,
  action,
  pct: isPct,
  color = 'default',
}: {
  label: string
  value: number | null | undefined
  suffix?: string
  sub?: string
  info?: React.ReactNode
  loading: boolean
  onClick?: () => void
  action?: React.ReactNode
  pct?: boolean
  color?: 'default' | 'green' | 'red'
}) => {
  const valueColor = {
    default: 'text-doubleNickel-gray-800',
    green: 'text-[#17B26A]',
    red: 'text-[#F04438]',
  }[color]

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white px-5 py-4 ${onClick ? 'cursor-pointer hover:border-gray-300 hover:bg-gray-50' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5">
        <p className="text-sm text-doubleNickel-gray-600">{label}</p>
        {info && (
          <Tooltip label={info} multiline w={260} withArrow position="top">
            <Info size={13} className="cursor-help text-gray-400" />
          </Tooltip>
        )}
      </div>
      {loading ? (
        <Skeleton height={32} width={80} mt={4} />
      ) : (
        <div className="mt-1 flex items-baseline gap-2">
          <p className={`text-2xl font-semibold ${valueColor}`}>
            {isPct ? fmtPct(value) : fmt(value)}
            {suffix && (
              <span className="ml-1 text-base font-medium text-gray-400">
                {suffix}
              </span>
            )}
          </p>
          {action}
        </div>
      )}
      {sub && <p className="mt-0.5 text-sm text-gray-500">{sub}</p>}
    </div>
  )
}

const SectionCard = ({
  title,
  sub,
  badge,
  loading,
  children,
  className = '',
  info,
}: {
  title: string
  sub?: string
  badge?: React.ReactNode
  loading: boolean
  children: React.ReactNode
  className?: string
  info?: React.ReactNode
}) => (
  <div className={`rounded-lg border border-gray-200 bg-white p-5 ${className}`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm font-semibold text-doubleNickel-gray-800">{title}</div>
        {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {info && (
          <Tooltip label={info} multiline w={280} withArrow position="top-end">
            <Info size={14} className="cursor-help text-gray-400" />
          </Tooltip>
        )}
        {badge}
      </div>
    </div>
    <div className="mt-4">
      {loading ? <Skeleton height={180} /> : children}
    </div>
  </div>
)

// Horizontal bar row with optional hover tooltip
const HBar = ({
  label,
  count,
  pct: pctWidth,
  color,
  tooltip,
}: {
  label: string
  count: number
  pct: number
  color: 'blue' | 'green' | 'red' | 'gray'
  tooltip?: React.ReactNode
}) => {
  const colorClass = {
    blue: 'bg-[#77BEE7]',
    green: 'bg-[#47CD89]',
    red: 'bg-[#FDA29B]',
    gray: 'bg-gray-300',
  }[color]

  return (
    <div className="group relative">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        <span className="text-xs font-semibold text-gray-600">{count.toLocaleString()}</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-gray-100">
        <div
          className={`h-2 rounded-full ${colorClass}`}
          style={{ width: `${Math.max(pctWidth, pctWidth > 0 ? 0.5 : 0)}%` }}
        />
      </div>
      {tooltip && (
        <div className="pointer-events-none absolute left-full top-0 z-20 ml-3 hidden w-48 rounded-lg border border-gray-200 bg-white p-3 shadow-lg group-hover:block">
          {tooltip}
        </div>
      )}
    </div>
  )
}


const CATEGORY_SERIES = [
  { name: 'autoApplied', color: '#77BEE7', label: 'Auto-filled',    def: 'AI auto-applied the value and it was not later corrected by a recruiter.' },
  { name: 'accepted',    color: '#47CD89', label: 'Accepted',       def: 'A recruiter reviewed the AI suggestion and confirmed it as-is (not later corrected).' },
  { name: 'corrections', color: '#FDA29B', label: 'Corrected',      def: 'AI was trusted (auto-filled or accepted) but a recruiter later changed the value. Every correction means the AI was wrong.' },
  { name: 'awaiting',    color: '#D0D5DD', label: 'Awaiting review', def: 'AI made a suggestion but no recruiter has acted on it yet, so it is excluded from accuracy.' },
  { name: 'dismissed',   color: '#EAECF0', label: 'Dismissed',      def: 'A recruiter reviewed an AI suggestion and rejected it, keeping the original value instead.' },
] as const

type CategoryChartRow = {
  category: string
  count: number
  autoApplied: number
  accepted: number
  corrections: number
  awaiting: number
  dismissed: number
}

const CategoryBarChart = ({ data }: { data: CategoryChartRow[] }) => (
  <>
    <BarChart
      h={240}
      data={data}
      dataKey="category"
      type="stacked"
      series={[...CATEGORY_SERIES]}
      tickLine="y"
      xAxisProps={{ angle: -30, textAnchor: 'end', height: 60 }}
      tooltipProps={{
        wrapperStyle: { background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 },
        cursor: { fill: 'rgba(0,0,0,0.04)' },
        content: ({ payload }) => {
          const item = payload?.[0]
          if (!item) return null
          const row = item.payload as CategoryChartRow
          const accuracyDenominator = row.autoApplied + row.accepted + row.corrections + row.dismissed
          const accuracyPct =
            accuracyDenominator > 0
              ? (((row.autoApplied + row.accepted) / accuracyDenominator) * 100).toFixed(1)
              : null
          return (
            <Paper px="sm" py="xs" withBorder shadow="sm" radius="md" className="bg-white">
              <p className="text-xs font-semibold text-gray-800">
                {row.category} — {row.count} total
              </p>
              {accuracyPct != null && (
                <p className="mb-1.5 text-xs text-[#17B26A]">{accuracyPct}% accurate</p>
              )}
              {CATEGORY_SERIES.map(({ name, label, color }) => (
                <div key={name} className="flex items-center justify-between gap-6 text-xs text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
                    {label}
                  </span>
                  <span className="font-semibold text-gray-800">{row[name]}</span>
                </div>
              ))}
            </Paper>
          )
        },
      }}
    />
    <div className="mt-2 flex flex-wrap gap-4 text-[10px] text-gray-500">
      {CATEGORY_SERIES.map(({ label, color, def }) => (
        <Tooltip key={label} label={def} multiline w={220} withArrow position="top">
          <span className="flex cursor-help items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
            {label}
          </span>
        </Tooltip>
      ))}
    </div>
  </>
)

// ── Page ──────────────────────────────────────────────────────────────────────

const makeDefaultStart = () => dayjs().subtract(1, 'month').startOf('day').toDate()
const makeDefaultEnd = () => new Date()

const DocumentIntelligenceAnalyticsPage = () => {
  const currentCompany = useCurrentCompanyContext()

  const [startDate, setStartDate] = useState<Date | null>(makeDefaultStart)
  const [endDate, setEndDate] = useState<Date | null>(makeDefaultEnd)
  const [granularity, setGranularity] = useState<'Day' | 'Week' | 'Month'>('Day')
  const [pendingModalOpen, setPendingModalOpen] = useState(false)
  const [correctionsModalOpen, setCorrectionsModalOpen] = useState(false)
  const [otherCategoriesOpen, setOtherCategoriesOpen] = useState(false)
  const [discrepancyModalOpen, setDiscrepancyModalOpen] = useState(false)

  const filters = {
    startDate: startDate ? dayjs(startDate).startOf('day').toISOString() : undefined,
    endDate: endDate ? dayjs(endDate).endOf('day').toISOString() : undefined,
  }

  const { data, loading } = useQuery(GET_DOCUMENT_INTELLIGENCE_STATS, {
    variables: { filters },
    fetchPolicy: 'cache-and-network',
    skip: !currentCompany?.documentIntelligenceEnabled,
  })

  const [fetchPending, { data: pendingData, loading: pendingLoading }] =
    useLazyQuery(GET_PENDING_REVIEW_DOCUMENTS, {
      variables: { filters },
      fetchPolicy: 'cache-and-network',
    })

  const [fetchCorrected, { data: correctedData, loading: correctedLoading }] =
    useLazyQuery(GET_CORRECTED_DOCUMENTS, {
      variables: { filters },
      fetchPolicy: 'cache-and-network',
    })

  const { data: discrepancyStatsData, loading: discrepancyStatsLoading } = useQuery(
    GET_DISCREPANCY_STATS,
    { variables: { filters }, fetchPolicy: 'cache-and-network', skip: !currentCompany?.documentIntelligenceEnabled }
  )

  const [fetchDiscrepancyDocs, { data: discrepancyDocsData, loading: discrepancyDocsLoading }] =
    useLazyQuery(GET_DISCREPANCY_DOCUMENTS, {
      variables: { filters },
      fetchPolicy: 'cache-and-network',
    })

  const { data: mvrDiscrepancyData } = useQuery(GET_MVR_DISCREPANCY_STATS, {
    variables: { filters },
    fetchPolicy: 'cache-and-network',
    skip: !currentCompany?.documentIntelligenceEnabled,
  })

  const { data: mvrSummaryData, loading: mvrSummaryLoading } = useQuery(
    GET_MVR_EXTRACTION_SUMMARY,
    {
      variables: { filters },
      fetchPolicy: 'cache-and-network',
      skip: !currentCompany?.documentIntelligenceEnabled,
    }
  )

  const mvrDiscrepancy = mvrDiscrepancyData?.documentIntelligenceMvrDiscrepancyStats
  const mvrSummary = mvrSummaryData?.documentIntelligenceMvrExtractionSummary

  // MVR drill-down for the discrepancy modal. Lazy because the modal is the
  // only consumer — no need to fire on page load.
  const [
    fetchMvrDiscrepancyDocs,
    { data: mvrDiscrepancyDocsData, loading: mvrDiscrepancyDocsLoading },
  ] = useLazyQuery(GET_MVR_DISCREPANCY_DOCUMENTS, {
    variables: { filters },
    fetchPolicy: 'cache-and-network',
  })

  const { data: pspDiscrepancyData } = useQuery(GET_PSP_DISCREPANCY_STATS, {
    variables: { filters },
    fetchPolicy: 'cache-and-network',
    skip: !currentCompany?.documentIntelligenceEnabled,
  })

  const { data: pspSummaryData, loading: pspSummaryLoading } = useQuery(
    GET_PSP_EXTRACTION_SUMMARY,
    {
      variables: { filters },
      fetchPolicy: 'cache-and-network',
      skip: !currentCompany?.documentIntelligenceEnabled,
    }
  )

  const pspDiscrepancy = pspDiscrepancyData?.documentIntelligencePspDiscrepancyStats
  const pspSummary = pspSummaryData?.documentIntelligencePspExtractionSummary

  const [
    fetchPspDiscrepancyDocs,
    { data: pspDiscrepancyDocsData, loading: pspDiscrepancyDocsLoading },
  ] = useLazyQuery(GET_PSP_DISCREPANCY_DOCUMENTS, {
    variables: { filters },
    fetchPolicy: 'cache-and-network',
  })

  const openPendingModal = () => {
    fetchPending()
    setPendingModalOpen(true)
  }

  const openCorrectionsModal = () => {
    fetchCorrected()
    setCorrectionsModalOpen(true)
  }

  const openDiscrepancyModal = () => {
    fetchDiscrepancyDocs()
    fetchMvrDiscrepancyDocs()
    fetchPspDiscrepancyDocs()
    setDiscrepancyModalOpen(true)
  }

  const stats = data?.documentIntelligenceStats
  const pendingDocs = pendingData?.pendingReviewDocuments ?? []
  const correctedDocs = correctedData?.correctedDocuments ?? []
  const discrepancyStats = discrepancyStatsData?.documentIntelligenceDiscrepancyStats
  const cdlDiscrepancyDocs = discrepancyDocsData?.documentIntelligenceDiscrepancyDocuments ?? []
  const mvrDiscrepancyDocs = mvrDiscrepancyDocsData?.documentIntelligenceMvrDiscrepancyDocuments ?? []
  const pspDiscrepancyDocs = pspDiscrepancyDocsData?.documentIntelligencePspDiscrepancyDocuments ?? []
  // Merge CDL/Medical mismatch rows with MVR Accident/Violation Count rows by
  // applicantId so an applicant with both types of discrepancy appears once,
  // with all mismatched fields and field-source links combined. Keep the newer
  // processedAt and that row's documentId as primary so the row sorts and
  // displays consistently.
  const discrepancyDocs = (() => {
    const byApplicant = new Map<string, typeof cdlDiscrepancyDocs[number]>()
    for (const doc of [
      ...cdlDiscrepancyDocs,
      ...mvrDiscrepancyDocs,
      ...pspDiscrepancyDocs,
    ]) {
      const existing = byApplicant.get(doc.applicantId)
      if (!existing) {
        byApplicant.set(doc.applicantId, { ...doc })
        continue
      }
      existing.mismatchedFields = Array.from(
        new Set([...existing.mismatchedFields, ...doc.mismatchedFields])
      )
      existing.fieldSources = [...existing.fieldSources, ...doc.fieldSources]
      if (new Date(doc.processedAt) > new Date(existing.processedAt)) {
        existing.processedAt = doc.processedAt
        existing.documentId = doc.documentId
      }
    }
    return Array.from(byApplicant.values()).sort(
      (a, b) => new Date(b.processedAt).valueOf() - new Date(a.processedAt).valueOf()
    )
  })()

  // ── Volume over time chart data ───────────────────────────────────────────

  type VolumeDataPoint = { date: string; count: number; categoryCount: number; expirationCount: number }

  const volumeData = (() => {
    const raw = stats?.volumeOverTime ?? []
    const rangeStart = dayjs(startDate ?? dayjs().subtract(1, 'month').toDate()).startOf('day')
    const rangeEnd = dayjs(endDate ?? new Date()).startOf('day')

    if (granularity === 'Day') {
      const lookup: Record<string, VolumeDataPoint> = {}
      raw.forEach((p) => { lookup[p.date] = { date: p.date, count: p.count, categoryCount: p.categoryCount ?? 0, expirationCount: p.expirationCount ?? 0 } })
      const days: VolumeDataPoint[] = []
      let cur = rangeStart
      while (!cur.isAfter(rangeEnd)) {
        const key = cur.format('YYYY-MM-DD')
        days.push({ date: cur.format('MMM D'), count: lookup[key]?.count ?? 0, categoryCount: lookup[key]?.categoryCount ?? 0, expirationCount: lookup[key]?.expirationCount ?? 0 })
        cur = cur.add(1, 'day')
      }
      return days
    }

    // Use year-aware keys internally to prevent cross-year collisions,
    // then produce human-friendly display labels separately.
    const buckets: Record<string, { count: number; categoryCount: number; expirationCount: number }> = {}
    raw.forEach(({ date, count, categoryCount, expirationCount }) => {
      const d = dayjs.utc(date)
      const key = granularity === 'Week'
        ? d.startOf('week').format('YYYY-MM-DD')
        : d.format('YYYY-MM')
      const b = buckets[key] ?? { count: 0, categoryCount: 0, expirationCount: 0 }
      buckets[key] = { count: b.count + count, categoryCount: b.categoryCount + (categoryCount ?? 0), expirationCount: b.expirationCount + (expirationCount ?? 0) }
    })

    const result: VolumeDataPoint[] = []
    if (granularity === 'Week') {
      let cur = rangeStart.startOf('week')
      while (!cur.isAfter(rangeEnd)) {
        const key = cur.format('YYYY-MM-DD')
        result.push({ date: cur.format('MMM D'), ...( buckets[key] ?? { count: 0, categoryCount: 0, expirationCount: 0 }) })
        cur = cur.add(1, 'week')
      }
    } else {
      let cur = rangeStart.startOf('month')
      while (!cur.isAfter(rangeEnd)) {
        const key = cur.format('YYYY-MM')
        result.push({ date: cur.format('MMM YYYY'), ...( buckets[key] ?? { count: 0, categoryCount: 0, expirationCount: 0 }) })
        cur = cur.add(1, 'month')
      }
    }
    return result
  })()

  // ── Category breakdown ────────────────────────────────────────────────────

  const categoryRows = stats?.categoryBreakdown ?? []

  // CDL and DOT Medical are the mature, high-accuracy categories.
  // All others are still being refined — shown separately with a disclaimer.
  const CORE_CATEGORIES = new Set(['CDL', 'DOT_MEDICAL'])

  const coreCatRows = categoryRows.filter((r) => CORE_CATEGORIES.has(r.categoryValue))
  const otherCatRows = categoryRows.filter((r) => !CORE_CATEGORIES.has(r.categoryValue))

  const toChartRow = (row: typeof categoryRows[number]) => ({
    category: catLabel(row.categoryValue),
    count: row.extracted,
    autoApplied: row.autoApplied,
    accepted: row.accepted,
    corrections: row.corrections,
    awaiting: row.awaiting,
    dismissed: row.dismissed,
  })

  const coreCategoryChartData = coreCatRows.map(toChartRow)
  const otherCategoryChartData = otherCatRows.map(toChartRow)

  // Core category totals (CDL + DOT Medical)
  const coreAutoApplied = coreCatRows.reduce((s, r) => s + r.autoApplied, 0)
  const coreAccepted    = coreCatRows.reduce((s, r) => s + r.accepted, 0)
  const coreCorrections = coreCatRows.reduce((s, r) => s + r.corrections, 0)
  const coreDismissed   = coreCatRows.reduce((s, r) => s + r.dismissed, 0)

  // Category-section stat cards: category-only accuracy and correction rate.
  // Must stay separate from the hero card so the percentage matches the bar chart.
  const catOnlyDenominator = coreAutoApplied + coreAccepted + coreCorrections + coreDismissed
  const coreCategoryAccuracyRate =
    catOnlyDenominator > 0 ? ((coreAutoApplied + coreAccepted) / catOnlyDenominator) * 100 : null
  const coreTrusted = coreAutoApplied + coreCorrections + coreAccepted
  const coreCorrectionRate = coreTrusted > 0 ? (coreCorrections / coreTrusted) * 100 : null

  // Hero card accuracy: CDL + DOT Medical, BOTH category and expiration predictions.
  // expAutoApplied/etc. are defined further below from coreExpiration* fields;
  // they're safe to reference here because JS hoists the const declarations from
  // the block above — actually they're not hoisted. We compute inline instead.
  const _coreExpAA = stats?.coreExpirationAutoApplied ?? 0
  const _coreExpAc = stats?.coreExpirationAccepted    ?? 0
  const _coreExpCo = stats?.coreExpirationCorrections ?? 0
  const _coreExpDi = stats?.coreExpirationDismissed   ?? 0
  const combinedNumerator   = coreAutoApplied + coreAccepted + _coreExpAA + _coreExpAc
  const combinedDenominator = coreAutoApplied + coreAccepted + coreCorrections + coreDismissed
                            + _coreExpAA + _coreExpAc + _coreExpCo + _coreExpDi
  const coreAccuracyRate =
    combinedDenominator > 0 ? (combinedNumerator / combinedDenominator) * 100 : null

  // ── Field counts (Fields Extracted chart) ─────────────────────────────────
  // byField includes all fields from raw SQL (not filtered to reviewable only)
  const fieldCounts = stats?.byField ?? []

  const REVIEWABLE = new Set(['CATEGORY', 'DATE'])
  const CDL_FIELDS = new Set(['LICENSE_NUMBER', 'LICENSE_STATE', 'LICENSE_CLASS', 'LICENSE_ENDORSEMENTS'])

  const reviewableFields = fieldCounts.filter((r) => REVIEWABLE.has(r.field))
  const cdlFields = fieldCounts.filter((r) => CDL_FIELDS.has(r.field))

  // ── Corrections Made rate (hero card) — all reviewable fields via byField ──
  const rvAutoApplied = reviewableFields.reduce((s, r) => s + r.autoAppliedCount, 0)
  const rvAccepted = reviewableFields.reduce((s, r) => s + (r.acceptedCount ?? 0), 0)
  const rvCorrected = reviewableFields.reduce((s, r) => s + (r.correctedCount ?? 0), 0)
  const correctionsDenominator = rvAutoApplied + rvAccepted + rvCorrected
  const correctionsRate =
    correctionsDenominator > 0 ? (rvCorrected / correctionsDenominator) * 100 : null

  // ── Expiration breakdown — CDL + DOT Medical only ────────────────────────

  const expAutoApplied = stats?.coreExpirationAutoApplied ?? 0
  const expAccepted    = stats?.coreExpirationAccepted    ?? 0
  const expAwaiting    = stats?.coreExpirationAwaiting    ?? 0
  const expDismissed   = stats?.coreExpirationDismissed   ?? 0
  const expCorrections = stats?.coreExpirationCorrections ?? 0

  const expAccuracyDenominator = expAutoApplied + expAccepted + expCorrections + expDismissed
  const expAccuracyRate =
    expAccuracyDenominator > 0
      ? ((expAutoApplied + expAccepted) / expAccuracyDenominator) * 100
      : null
  const expTrusted = expAutoApplied + expCorrections + expAccepted
  const expCorrectionRate = expTrusted > 0 ? (expCorrections / expTrusted) * 100 : null

  // ── Discrepancy chart data (CDL fields + MVR rows) ───────────────────────
  //
  // The Accident/Violation Count bar covers MVR + PSP mismatches combined.
  // Both surfaces measure the same semantic thing — the applicant's
  // AI-extracted driving-event count vs. what they self-disclosed on their
  // application — so surfacing them as separate bars would fragment a single
  // "did the driver under-disclose events" signal. Sum instead. The rare
  // overlap (applicant flagged by both MVR and PSP) means a very small
  // over-count in the bar height; not worth a distinct-union backend query
  // for a directional chart. PSP admin-gated for now.
  const discrepancyChartData = [
    ...(discrepancyStats?.byField ?? []).map((f) => ({
      field: FIELD_LABELS[f.field] ?? f.field,
      count: f.count,
    })),
    // MVR + PSP combined accident/violation count. The unit differs from
    // CDL bars (applicants-with-mismatch vs. documents-with-mismatch) —
    // the info popover explains this.
    ...(mvrDiscrepancy
      ? [
          {
            field: FIELD_LABELS.INCIDENTS_COUNT,
            count:
              mvrDiscrepancy.incidentsCountMismatchCount +
              (pspDiscrepancy?.incidentsCountMismatchCount ?? 0),
          },
          // ENG-4343: applicants who said No to "ever suspended/revoked/
          // denied?" on the application but whose current MVR shows one
          // or more SUSPENSION or LICENSE_ACTION findings.
          {
            field: FIELD_LABELS.SUSPENSION_UNDISCLOSED,
            count: mvrDiscrepancy.suspensionMismatchCount,
          },
        ]
      : []),
  ]

  // ── Time saved estimate ───────────────────────────────────────────────────
  const timeSavedSeconds = (stats?.documentsProcessed ?? 0) * SECONDS_PER_DOCUMENT
  const timeSavedHours = timeSavedSeconds / 3600
  const timeSavedValue = timeSavedHours >= 1
    ? Math.round(timeSavedHours * 10) / 10
    : Math.round(timeSavedSeconds / 60)
  const timeSavedUnit = timeSavedHours >= 1 ? 'hrs' : 'min'

  if (currentCompany && !currentCompany.documentIntelligenceEnabled) {
    navigate(routes.dashboard())
    return null
  }

  return (
    <div className="flex h-full flex-col gap-5 overflow-auto px-5 pb-8">
        <Metadata
          title="Document Intelligence Analytics"
          description="Track Double Nickel document processing volume, AI accuracy, and hours saved on manual review"
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-doubleNickel-gray-800">
              Document Intelligence
            </div>
            <p className="mt-0.5 text-sm text-gray-500">
              Track Double Nickel document processing volume, AI accuracy, and hours saved on manual review
            </p>
          </div>
          <DateRangeMenu
            startDate={startDate}
            endDate={endDate}
            handleChange={(value) => {
              setStartDate(value[0])
              setEndDate(value[1])
            }}
          />
        </div>

        {/* Row 1: Documents Analyzed graph + stacked [Documents Analyzed card,
            Time Saved card]. The graph dominates the row; the two stat tiles
            stack vertically on the right to keep volume context visible at
            the top of the page. */}
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-semibold text-doubleNickel-gray-800">
                  Documents Analyzed
                </div>
                <p className="mt-0.5 text-xs text-gray-500">Number of documents AI processed per period</p>
              </div>
              <SegmentedControl
                size="xs"
                value={granularity}
                onChange={(v) => setGranularity(v as typeof granularity)}
                data={[
                  { label: 'Daily', value: 'Day' },
                  { label: 'Weekly', value: 'Week' },
                  { label: 'Monthly', value: 'Month' },
                ]}
              />
            </div>
            <div className="mt-4">
              {loading && !stats ? (
                <Skeleton height={240} />
              ) : volumeData.every((d) => d.count === 0) ? (
                <div className="flex h-60 items-center justify-center text-sm text-gray-400">
                  No data for selected period
                </div>
              ) : (
                <BarChart
                  h={240}
                  data={volumeData}
                  dataKey="date"
                  series={[{ name: 'count', color: '#77BEE7', label: 'Documents' }]}
                  tickLine="y"
                  barProps={{ activeBar: { fill: '#77BEE7', opacity: 0.75 } }}
                  tooltipProps={{
                    wrapperStyle: { background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 },
                    cursor: { fill: 'rgba(0,0,0,0.04)' },
                    content: ({ payload }) => {
                      const item = payload?.[0]
                      if (!item || item.value === 0) return null
                      const p = item.payload as VolumeDataPoint
                      return (
                        <Paper px="sm" py="xs" withBorder shadow="sm" radius="md" className="bg-white">
                          <p className="mb-1 text-xs font-semibold text-gray-800">{p.date}</p>
                          <div className="flex flex-col gap-0.5 text-xs text-gray-600">
                            <span className="font-semibold text-gray-900">{p.count} documents analyzed</span>
                            {p.categoryCount > 0 && <span>{p.categoryCount} categories identified</span>}
                            {p.expirationCount > 0 && <span>{p.expirationCount} expiration dates extracted</span>}
                          </div>
                        </Paper>
                      )
                    },
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <StatCard
              label="Documents Analyzed"
              value={stats?.documentsProcessed}
              info="Documents the AI successfully scanned and extracted data from."
              loading={loading}
            />
            <StatCard
              label="Time Saved"
              value={stats ? timeSavedValue : undefined}
              suffix={timeSavedUnit}
              info="Estimated time saved at 45 seconds per document analyzed."
              loading={loading}
            />
          </div>
        </div>

        {/* Main content stack: Discrepancies → combined Fields Extracted →
            Category/Expiration extractions header → Category + Expiration →
            accuracy cards at the very bottom. */}
        <div className="flex flex-col gap-4">

          {/* Document and Application Discrepancies — surfaced first because
              this is the highest-signal "something to act on" view. Per-doc-
              type rates surface CDL / DOT Medical / MVR independently so an
              outlier in one category doesn't dilute the others. */}
          <SectionCard
            title="Document and Application Discrepancies"
            sub="CDL, DOT Medical, MVR, and PSP documents"
            loading={discrepancyStatsLoading && !discrepancyStats}
            info={
              <div className="space-y-1.5 text-xs">
                <p className="font-semibold">What counts as a discrepancy?</p>
                <p>A discrepancy is when a value AI read from a document differs from what the applicant submitted in their application form.</p>
                <p className="pt-1">Each per-doc-type tile shows the rate independently — a CDL mismatch doesn't move the DOT Medical rate.</p>
                <p className="pt-1">Each bar shows the number of documents (or applicants, for MVR) where that specific field mismatched. One document can contribute to multiple bars.</p>
                <p className="pt-1"><span className="font-semibold text-white">License Number</span> — normalized comparison (special chars removed, case-insensitive).</p>
                <p><span className="font-semibold text-white">State / Class</span> — case-insensitive exact match.</p>
                <p><span className="font-semibold text-white">Endorsements</span> — sorted set comparison.</p>
                <p><span className="font-semibold text-white">Expiration Date</span> — CDL expiration, UTC day comparison.</p>
                <p><span className="font-semibold text-white">Medical Expiration</span> — DOT Medical expiration vs. applicant-submitted medical exam date.</p>
                <p><span className="font-semibold text-white">Accident/Violation Count</span> — applicants whose AI-extracted incident count (accidents + moving + non-moving violations from the MVR, plus crashes + violations from the FMCSA PSP report) exceeds the number of accidents they self-disclosed on the Accidents &amp; Violations card (hidden by the applicant). Under-disclosure by the report — extracted count lower than disclosed, typically because older events aged out of FMCSA's lookback window — is not counted. Counted once per applicant.</p>
                <p><span className="font-semibold text-white">Undisclosed Suspension</span> — applicants who answered "No" to "Has your license ever been suspended, revoked, or denied?" on the application but whose current MVR shows one or more suspension or license-action findings. Counted once per applicant.</p>
              </div>
            }
          >
            {discrepancyStats &&
             (discrepancyStats.applicantsInScope ?? 0) === 0 &&
             // applicantsInScope is the CDL + DOT Medical + MVR backend union.
             // PSP applicants aren't in it, so a date range with only stamped
             // PSP applicants (and no mismatches) would otherwise render the
             // empty state even though the PSP tile has data. Fold PSP scope
             // in so the empty state only fires when truly nothing to show.
             (pspDiscrepancy?.applicantsWithPsp ?? 0) === 0 &&
             discrepancyChartData.every((d) => d.count === 0) ? (
              <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                No documents with applicant data for this period.
              </div>
            ) : (
              <>
                {/* Per-doc-type tiles. Each renders independently — its
                    denominator is applicants with that doc type, its
                    numerator is applicants with a mismatch of that type. */}
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <p className="text-xs text-gray-500">
                    % of documents whose AI-read data didn't match the applicant's submission
                  </p>
                  <button
                    className="shrink-0 text-xs text-blue-600 underline hover:text-blue-700"
                    onClick={openDiscrepancyModal}
                  >
                    View documents →
                  </button>
                </div>
                <div className="mb-4 grid grid-cols-4 gap-3">
                  {[
                    {
                      label: 'CDL',
                      numerator: discrepancyStats?.cdlApplicantsWithDiscrepancy,
                      denominator: discrepancyStats?.cdlApplicantsInScope,
                      rate: discrepancyStats?.cdlDiscrepancyRate,
                      unitNoun: 'documents',
                    },
                    {
                      label: 'DOT Medical',
                      numerator: discrepancyStats?.medicalApplicantsWithDiscrepancy,
                      denominator: discrepancyStats?.medicalApplicantsInScope,
                      rate: discrepancyStats?.medicalDiscrepancyRate,
                      unitNoun: 'documents',
                    },
                    {
                      label: 'MVR',
                      // ENG-4343: distinct applicants flagged for either an
                      // incidents-count mismatch OR an undisclosed suspension.
                      // anyMismatchCount is deduplicated on the backend so
                      // applicants with both aren't counted twice.
                      numerator: mvrDiscrepancy?.anyMismatchCount,
                      denominator: mvrDiscrepancy?.applicantsWithMvr,
                      rate:
                        mvrDiscrepancy &&
                        (mvrDiscrepancy.applicantsWithMvr ?? 0) > 0
                          ? ((mvrDiscrepancy.anyMismatchCount ?? 0) /
                              mvrDiscrepancy.applicantsWithMvr) *
                            100
                          : null,
                      // MVR comparison is per-applicant (one applicant with
                      // mismatched MVR-vs-application data), unlike CDL /
                      // Medical which are per-doc.
                      unitNoun: 'applicants',
                    },
                    {
                      label: 'PSP',
                      // Distinct applicants whose PSP-extracted event count
                      // (CRASH + VIOLATION findings) differs from their
                      // disclosed Accident count.
                      numerator: pspDiscrepancy?.anyMismatchCount,
                      denominator: pspDiscrepancy?.applicantsWithPsp,
                      rate:
                        pspDiscrepancy &&
                        (pspDiscrepancy.applicantsWithPsp ?? 0) > 0
                          ? ((pspDiscrepancy.anyMismatchCount ?? 0) /
                              pspDiscrepancy.applicantsWithPsp) *
                            100
                          : null,
                      unitNoun: 'applicants',
                    },
                  ].map((tile) => (
                    <Tooltip
                      key={tile.label}
                      label={
                        tile.numerator != null && tile.denominator != null
                          ? `${fmt(tile.numerator)} of ${fmt(tile.denominator)} ${tile.unitNoun} mismatched`
                          : 'No data for this period'
                      }
                      withArrow
                      position="top"
                    >
                      <div className="cursor-help rounded-lg border border-gray-200 bg-white px-4 py-3">
                        <p className="text-xs text-doubleNickel-gray-600">{tile.label}</p>
                        <p className="mt-1 text-lg font-semibold text-doubleNickel-gray-800">
                          {tile.rate != null ? `${fmtPct(tile.rate)}%` : '—'}
                        </p>
                      </div>
                    </Tooltip>
                  ))}
                </div>
                {discrepancyChartData.every((d) => d.count === 0) ? (
                  <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                    No discrepancies found for this period.
                  </div>
                ) : (
                  <BarChart
                    h={200}
                    data={discrepancyChartData}
                    dataKey="field"
                    series={[{ name: 'count', color: '#77BEE7', label: 'Documents' }]}
                    tickLine="y"
                    barProps={{ activeBar: { fill: '#77BEE7', opacity: 0.75 } }}
                    tooltipProps={{
                      wrapperStyle: { background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 },
                      cursor: { fill: 'rgba(0,0,0,0.04)' },
                      content: ({ payload }) => {
                        const item = payload?.[0]
                        if (!item || item.value === 0) return null
                        // The Accident/Violation Count bar (MVR + PSP
                        // combined) and Undisclosed Suspension bar are keyed
                        // per applicant; every other bar is keyed per
                        // document.
                        const isApplicantsBar =
                          item.payload.field === FIELD_LABELS.INCIDENTS_COUNT ||
                          item.payload.field === FIELD_LABELS.SUSPENSION_UNDISCLOSED
                        const noun = isApplicantsBar ? 'applicant' : 'document'
                        return (
                          <Paper px="sm" py="xs" withBorder shadow="sm" radius="md" className="bg-white">
                            <p className="text-xs font-semibold text-gray-800">{item.payload.field}</p>
                            <p className="text-xs text-gray-600">{item.value} {noun}{item.value === 1 ? '' : 's'} with mismatch</p>
                          </Paper>
                        )
                      },
                    }}
                  />
                )}
                <p className="mt-2 text-[10px] text-gray-400">One document may contribute to multiple bars.</p>
              </>
            )}
          </SectionCard>

          {/* Combined Fields Extracted — editable + CDL fields (per-field
              prediction counts) plus MVR + PSP finding-type rollups in one
              card. All bars are scaled to a single global max so a small
              section's bars are directly comparable to a large section's. */}
          <SectionCard
            title="Fields Extracted"
            sub="All predictions and MVR/PSP findings extracted by the AI"
            loading={loading && !stats}
          >
            {(() => {
              const mvrRows = [
                { label: 'Accidents',             count: mvrSummary?.accidentCount             ?? 0 },
                { label: 'Moving violations',     count: mvrSummary?.movingViolationCount      ?? 0 },
                { label: 'Non-moving violations', count: mvrSummary?.nonMovingViolationCount   ?? 0 },
                { label: 'Suspensions',           count: mvrSummary?.suspensionCount           ?? 0 },
                { label: 'Disqualifications',    count: mvrSummary?.disqualificationCount     ?? 0 },
                { label: 'License actions',       count: mvrSummary?.licenseActionCount        ?? 0 },
                { label: 'Medical certificates',  count: mvrSummary?.medicalCertCount          ?? 0 },
              ]
              const pspRows = [
                { label: 'Crashes',     count: pspSummary?.crashCount      ?? 0 },
                { label: 'Inspections', count: pspSummary?.inspectionCount ?? 0 },
                { label: 'Violations',  count: pspSummary?.violationCount  ?? 0 },
              ]
              // Single shared max across editable, CDL, MVR, and PSP rows so
              // all bars are directly comparable. Without this the smaller
              // groups would always look full-width regardless of true magnitude.
              const globalMax = Math.max(
                1,
                ...reviewableFields.map((r) => r.totalPredictions),
                ...cdlFields.map((r) => r.totalPredictions),
                ...mvrRows.map((r) => r.count),
                ...pspRows.map((r) => r.count),
              )
              return (
                <div className="flex flex-col gap-2.5">
                  {reviewableFields.map((row) => (
                    <HBar
                      key={row.field}
                      label={FIELD_LABELS[row.field] ?? row.field}
                      count={row.totalPredictions}
                      pct={(row.totalPredictions / globalMax) * 100}
                      color="blue"
                    />
                  ))}
                  {cdlFields.length > 0 && (
                    <>
                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        From CDL documents
                      </p>
                      {cdlFields.map((row) => (
                        <HBar
                          key={row.field}
                          label={FIELD_LABELS[row.field] ?? row.field}
                          count={row.totalPredictions}
                          pct={(row.totalPredictions / globalMax) * 100}
                          color="blue"
                        />
                      ))}
                    </>
                  )}
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    From MVR documents
                  </p>
                  {/* Gate MVR rows on the MVR-summary query specifically.
                      Outer SectionCard's loading prop only tracks the main
                      stats query, so without this branch the MVR rows
                      would briefly render flat zeros (?? 0) when stats
                      finish before the MVR query completes. */}
                  {mvrSummaryLoading && !mvrSummary
                    ? Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton key={`mvr-skel-${i}`} height={28} />
                      ))
                    : mvrRows.map((row) => (
                        <HBar
                          key={row.label}
                          label={row.label}
                          count={row.count}
                          pct={(row.count / globalMax) * 100}
                          color="blue"
                        />
                      ))}
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    From PSP documents
                  </p>
                  {pspSummaryLoading && !pspSummary
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={`psp-skel-${i}`} height={28} />
                      ))
                    : pspRows.map((row) => (
                        <HBar
                          key={row.label}
                          label={row.label}
                          count={row.count}
                          pct={(row.count / globalMax) * 100}
                          color="blue"
                        />
                      ))}
                </div>
              )
            })()}
          </SectionCard>

          {/* Category and Expiration Date Extractions header — these two
              sections cover the recruiter-facing review surface (the only
              fields that can be auto-applied / accepted / corrected /
              dismissed today). The three accuracy stat cards sit directly
              under the header because they only describe these two fields. */}
          <div className="mt-2">
            <h2 className="text-base font-semibold text-doubleNickel-gray-800">
              Category and Expiration Date Extractions
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              How recruiters acted on AI suggestions for the two reviewable fields
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              label="Accurate Predictions"
              value={coreAccuracyRate}
              suffix="%"
              pct
              info={
                <div className="space-y-2 text-xs">
                  <p>Accuracy across CDL and DOT Medical documents — category and expiration date predictions combined.</p>
                  <p className="font-semibold">Formula: (Auto-filled + Accepted) ÷ (Auto-filled + Accepted + Corrected + Dismissed)</p>
                  <p>Only acted-on predictions are included. <span className="font-semibold text-white">Awaiting review</span> is excluded until a recruiter makes a decision.</p>
                </div>
              }
              loading={loading}
            />
            <StatCard
              label="Corrections Made"
              value={correctionsRate}
              suffix="%"
              pct
              info="Corrections % = Corrected ÷ (Auto-filled + Accepted + Corrected). Lower is better — AI was trusted but later proved wrong. Click to see the list."
              loading={loading}
              onClick={openCorrectionsModal}
            />
            <StatCard
              label="Needs Review"
              value={
                stats && (stats.documentsProcessed ?? 0) > 0
                  ? ((stats.pendingReviewCount ?? 0) / stats.documentsProcessed) * 100
                  : stats
                    ? 0
                    : undefined
              }
              suffix="%"
              pct
              info="Documents with at least one AI suggestion your team hasn't reviewed yet. Click to see the list."
              loading={loading}
              onClick={openPendingModal}
            />
          </div>

          {/* Document Type — Category */}
          <SectionCard
            title="Document Type — Category"
            sub="CDL and DOT Medical predictions — bar segments show how each prediction was handled"
            loading={loading && !stats}
            info={
              <div className="space-y-1.5 text-xs">
                <p className="font-semibold">How to read this chart</p>
                <p>Each bar shows predictions for one document category, split into 5 mutually exclusive states. Hover a bar for exact counts and accuracy.</p>
                <p className="pt-1 font-semibold">The 5 states</p>
                <p><span className="font-semibold text-white">Auto-filled</span> — AI applied the value automatically and it was not later corrected.</p>
                <p><span className="font-semibold text-white">Accepted</span> — Recruiter confirmed the AI suggestion as-is (not later corrected).</p>
                <p><span className="font-semibold text-white">Corrected</span> — AI was trusted but a recruiter later changed the value.</p>
                <p><span className="font-semibold text-white">Awaiting review</span> — No recruiter decision yet; excluded from accuracy.</p>
                <p><span className="font-semibold text-white">Dismissed</span> — Recruiter rejected the suggestion.</p>
                <p className="pt-1">Accuracy includes only acted-on predictions, so <span className="font-semibold text-white">Awaiting review</span> is intentionally excluded from the denominator.</p>
              </div>
            }
          >
            <div className="mb-4 grid grid-cols-3 gap-3">
              {[
                { label: 'Categories identified', value: fmt(coreCatRows.reduce((s, r) => s + r.extracted, 0)) },
                { label: 'Accurate Predictions', value: coreCategoryAccuracyRate != null ? `${fmtPct(coreCategoryAccuracyRate)}%` : '—' },
                { label: 'Correction rate', value: coreCorrectionRate != null ? `${fmtPct(coreCorrectionRate)}%` : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs text-doubleNickel-gray-600">{label}</p>
                  <p className="mt-1 text-lg font-semibold text-doubleNickel-gray-800">{value}</p>
                </div>
              ))}
            </div>
            {coreCategoryChartData.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-gray-400">
                No data for selected period
              </div>
            ) : (
              <>
                <CategoryBarChart data={coreCategoryChartData} />
              </>
            )}

            {/* Other categories — still in development */}
            {otherCatRows.length > 0 && (
              <div className="mt-5 border-t border-gray-100 pt-4">
                <button
                  className="flex w-full items-center justify-between text-left"
                  onClick={() => setOtherCategoriesOpen((o) => !o)}
                >
                  <div className="flex items-center gap-2">
                    {otherCategoriesOpen ? (
                      <ChevronDown size={14} className="text-gray-400" />
                    ) : (
                      <ChevronRight size={14} className="text-gray-400" />
                    )}
                    <span className="text-xs font-medium text-gray-600">
                      Other document categories
                    </span>
                    <Badge color="yellow" variant="light" size="xs">
                      AI accuracy still improving
                    </Badge>
                  </div>
                  <span className="text-xs text-gray-400">{otherCatRows.length} {otherCatRows.length === 1 ? 'category' : 'categories'}</span>
                </button>
                <Collapse in={otherCategoriesOpen}>
                  <p className="mb-3 mt-2 text-xs text-gray-400">
                    These document types are still being refined. Accuracy data is provided for visibility, but is not yet reflected in the top-line metrics.
                  </p>
                  <CategoryBarChart data={otherCategoryChartData} />
                </Collapse>
              </div>
            )}
          </SectionCard>

          {/* Expiration Date */}
          <SectionCard
            title="Expiration Date"
            sub="AI-extracted expiration dates from CDL and DOT Medical documents"
            loading={loading && !stats}
            info={
              <div className="space-y-1.5 text-xs">
                <p className="font-semibold">How to read these numbers</p>
                <p>Counts only expiration date predictions on CDL and DOT Medical documents. The same 5 states apply: Auto-filled, Accepted, Corrected, Awaiting review, and Dismissed.</p>
                <p className="pt-1"><span className="font-semibold text-white">Accurate Predictions %</span> — (Auto-filled + Accepted) ÷ (Auto-filled + Accepted + Corrected + Dismissed)</p>
                <p><span className="font-semibold text-white">Correction rate %</span> — Corrected ÷ (Auto-filled + Accepted + Corrected). Lower is better.</p>
                <p><span className="font-semibold text-white">Awaiting review</span> is excluded because no recruiter decision has been made yet.</p>
              </div>
            }
          >
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Dates extracted', value: fmt(expAutoApplied + expAccepted + expAwaiting + expDismissed + expCorrections) },
                { label: 'Accurate Predictions', value: expAccuracyRate != null ? `${fmtPct(expAccuracyRate)}%` : '—' },
                { label: 'Correction rate', value: expCorrectionRate != null ? `${fmtPct(expCorrectionRate)}%` : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <p className="text-xs text-doubleNickel-gray-600">{label}</p>
                  <p className="mt-1 text-lg font-semibold text-doubleNickel-gray-800">{value}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

      {/* Pending review modal */}
      <Modal
        opened={pendingModalOpen}
        onClose={() => setPendingModalOpen(false)}
        title={
          <div>
            <div className="text-sm font-semibold text-doubleNickel-gray-800">Pending Review</div>
            <p className="mt-0.5 text-xs text-gray-500">
              Documents with unreviewed AI predictions — click a row to open the applicant
            </p>
          </div>
        }
        size="xl"
      >
        {pendingLoading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : pendingDocs.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            No documents pending review for this date range.
          </div>
        ) : (
          <Table horizontalSpacing="md" verticalSpacing="sm" className="text-sm">
            <Table.Thead>
              <Table.Tr className="text-xs text-doubleNickel-gray-500">
                <Table.Th>Applicant</Table.Th>
                <Table.Th>Document Type</Table.Th>
                <Table.Th>Pending Fields</Table.Th>
                <Table.Th className="text-right">Processed</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pendingDocs.map((doc) => (
                <Table.Tr
                  key={doc.documentId}
                  className="cursor-pointer text-doubleNickel-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    if (!doc.applicantId) return
                    setPendingModalOpen(false)
                    navigate(routes.applicantDetails({ id: doc.applicantId, tab: 'documents', subtab: 'documents', documentId: doc.documentId }))
                  }}
                >
                  <Table.Td className="font-medium">{doc.applicantName}</Table.Td>
                  <Table.Td>
                    {doc.category ? catLabel(doc.category) : '—'}
                  </Table.Td>
                  <Table.Td>
                    <div className="flex flex-wrap gap-1">
                      {doc.pendingFields.map((f) => (
                        <Badge key={f} size="xs" variant="light" color="yellow">
                          {FIELD_LABELS[f] ?? f}
                        </Badge>
                      ))}
                    </div>
                  </Table.Td>
                  <Table.Td className="text-right text-gray-400">
                    {dayjs(doc.createdAt).format('MMM D, YYYY')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Modal>
      {/* Corrections modal */}
      <Modal
        opened={correctionsModalOpen}
        onClose={() => setCorrectionsModalOpen(false)}
        title={
          <div>
            <div className="text-sm font-semibold text-doubleNickel-gray-800">Corrections Made</div>
            <p className="mt-0.5 text-xs text-gray-500">
              Documents where a recruiter changed a value AI had already applied — click a row to open the applicant
            </p>
          </div>
        }
        size="xl"
      >
        {correctedLoading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : correctedDocs.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            No corrections for this date range.
          </div>
        ) : (
          <Table horizontalSpacing="md" verticalSpacing="sm" className="text-sm">
            <Table.Thead>
              <Table.Tr className="text-xs text-doubleNickel-gray-500">
                <Table.Th>Applicant</Table.Th>
                <Table.Th>Document Type</Table.Th>
                <Table.Th>Corrected Fields</Table.Th>
                <Table.Th className="text-right">Processed</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {correctedDocs.map((doc) => (
                <Table.Tr
                  key={doc.documentId}
                  className="cursor-pointer text-doubleNickel-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    if (!doc.applicantId) return
                    setCorrectionsModalOpen(false)
                    navigate(routes.applicantDetails({ id: doc.applicantId, tab: 'documents', subtab: 'documents', documentId: doc.documentId }))
                  }}
                >
                  <Table.Td className="font-medium">{doc.applicantName}</Table.Td>
                  <Table.Td>
                    {doc.category ? catLabel(doc.category) : '—'}
                  </Table.Td>
                  <Table.Td>
                    <div className="flex flex-wrap gap-1">
                      {doc.correctedFields.map((f) => (
                        <Badge key={f} size="xs" variant="light" color="red">
                          {FIELD_LABELS[f] ?? f}
                        </Badge>
                      ))}
                    </div>
                  </Table.Td>
                  <Table.Td className="text-right text-gray-400">
                    {dayjs.utc(doc.createdAt).format('MMM D, YYYY')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Modal>
      {/* CDL Discrepancies modal */}
      <Modal
        opened={discrepancyModalOpen}
        onClose={() => setDiscrepancyModalOpen(false)}
        title={
          <div>
            <div className="text-sm font-semibold text-doubleNickel-gray-800">CDL Discrepancies</div>
            <p className="mt-0.5 text-xs text-gray-500">
              Documents where AI-read CDL data differed from applicant-submitted data — click a row to open the applicant
            </p>
          </div>
        }
        size="xl"
      >
        {discrepancyDocsLoading || mvrDiscrepancyDocsLoading || pspDiscrepancyDocsLoading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : discrepancyDocs.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            No discrepancies found for this date range.
          </div>
        ) : (
          <Table horizontalSpacing="md" verticalSpacing="sm" className="text-sm">
            <Table.Thead>
              <Table.Tr className="text-xs text-doubleNickel-gray-500">
                <Table.Th>Applicant</Table.Th>
                <Table.Th>Mismatched Fields</Table.Th>
                <Table.Th className="text-right">Processed</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {discrepancyDocs.map((doc) => (
                <Table.Tr
                  key={doc.documentId}
                  className="cursor-pointer text-doubleNickel-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    if (!doc.applicantId) return
                    setDiscrepancyModalOpen(false)
                    navigate(routes.applicantDetails({ id: doc.applicantId, tab: 'application' }))
                  }}
                >
                  <Table.Td className="font-medium">{doc.applicantName}</Table.Td>
                  <Table.Td>
                    <div className="flex flex-wrap gap-1">
                      {doc.fieldSources.map((source) => (
                        <Badge
                          key={source.field}
                          size="xs"
                          variant="light"
                          color="yellow"
                          className="cursor-pointer hover:opacity-75"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!doc.applicantId) return
                            setDiscrepancyModalOpen(false)
                            navigate(routes.applicantDetails({ id: doc.applicantId, tab: 'documents', subtab: 'documents', documentId: source.documentId }))
                          }}
                        >
                          {FIELD_LABELS[source.field] ?? source.field}
                        </Badge>
                      ))}
                    </div>
                  </Table.Td>
                  <Table.Td className="text-right text-gray-400">
                    {dayjs.utc(doc.processedAt).format('MMM D, YYYY')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Modal>
    </div>
  )
}

export default DocumentIntelligenceAnalyticsPage
